import { equals, IPromise } from "angular";
import { Stage, Layer, Group } from "konva/lib/Core";
import { IReactionSettings, ISurface, IWhiteboardState } from "../models";
import { WhiteboardEvent, WhiteboardEventObject, addStateEventListener } from "./events";
import { IWhiteboardShapeState, shapes, IPosition, WhiteboardShape, zeroPosition, ISize, selectionBoundaries, areaSize, scalePositions } from "./shapes";
import { IWhiteboardLinkState, links, ILink, linkId, WhiteboardLink } from "./links";
import { IAssignee } from "@gtmhub/assignees";
import { px } from "./util";
import { IShapeSelectionRenderer, configureShapeSelection } from "./behaviors/selection";
import { configureShapeContextMenu } from "./behaviors/shape-context-menu";
import { IRect } from "konva/types/types";
import { IUIError } from "@gtmhub/error-handling/models";
import { ICreateReaction, IReaction } from "@gtmhub/experiments/models/models";
import { WhiteboardEvents } from "@gtmhub/whiteboards";
import { sortMetricsByOrderId } from "./shapes/goal";

export const whiteboardRendererFactory = {
  create(container: HTMLDivElement, context: IWhiteboardRendererContext, options?: { zoomRatio: number }): WhiteboardRenderer {
    return new WhiteboardRenderer(container, context, options);
  },
};

const setScrollBehavior = (element: HTMLDivElement, behavior: "smooth" | "auto") => {
  element.style.scrollBehavior = behavior;
};

const createShapeInstanceFromState = (state: IWhiteboardShapeState, context: IWhiteboardRendererContext, stage, layer): WhiteboardShape => {
  for (const ctor of shapes) {
    if (ctor.stateTypes.includes(state.type)) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return new (ctor as any)(state, context, stage, layer);
      } catch (error) {
        console.error(`Cannot instantiate shape ${state.type} ${state.id}`, error);
        return null;
      }
    }
  }

  console.warn("Unknown shape", state);
  return null;
};

const createLinkInstanceFromState = (state: IWhiteboardLinkState, context: IWhiteboardRendererContext): Group & ILink<IWhiteboardLinkState> => {
  for (const ctor of links) {
    if (ctor.stateTypes.includes(state.type)) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return new (ctor as any)(state, context);
      } catch (error) {
        console.error(`Cannot instantiate link ${state.type}`, error);
        return null;
      }
    }
  }

  console.warn("Unknown link", state);
  return null;
};

const defaultRenderOptions: IWhiteboardRenderOptions = {};

const contextMenuTooltipTopMargin = 23; // Make room for context menu tooltips

// if you want to speed up the scroll increase minResizeCanvasContainerDistance value
const minResizeCanvasContainerDistance = 5;
const ALLOW_DRAG_SHAPE_TIMEOUT = 1;
let STOP_DRAG_SHAPE = false;

// reset the flag after ALLOW_DRAG_SHAPE_TIMEOUT ms to simulate smooth scroll
const resetAbilityToDragShape = (): void => {
  setTimeout(() => (STOP_DRAG_SHAPE = false), ALLOW_DRAG_SHAPE_TIMEOUT);
};

export interface IWhiteboardRendererContext {
  assignees(id: string): IAssignee;
  readonly: boolean;
  whiteboardVotingEnabled?: boolean;
  whiteboardBasicShapesEnabled?: boolean;
  whiteboardSessionManagementEnabled?: boolean;
  hoverAssigneeIds?(ids: string[]): void;
  generateFileUrl?(url: string): IPromise<string>;
  createReaction(createReaction: ICreateReaction): Promise<IReaction>;
  deleteReaction(reactionName: string, targetId: string): Promise<void>;
  showErrorModal(error: IUIError);
  whiteboardId: string;
  events?: WhiteboardEvents;
  reactionSettings?: IReactionSettings;
  setPreventDblClick(prevent: boolean): void;
  getPreventDblClick(): boolean;
}

export const viewportMargin = { topBottom: 70, leftRight: 130 };

export interface IWhiteboardRenderOptions {
  keepCurrentViewport?: boolean;
  shapeId?: string;
}

export interface IWhiteboardRenderer {
  render(state: IWhiteboardState, options?: IWhiteboardRenderOptions): void;
  renderSingleShape(state: IWhiteboardState, shapeId: string, options?: IWhiteboardRenderOptions): void;
}

export interface WhiteboardRenderer extends IShapeSelectionRenderer, IWhiteboardRenderer {}

export interface AdjustScrollParams {
  viewportX: number;
  viewportY: number;
  maxScrollTop: number;
  maxScrollLeft: number;
  isInLeftEdge: boolean;
  isInRightEdge: boolean;
  isInTopEdge: boolean;
  isInBottomEdge: boolean;
  wbTopEdge: number;
  wbLeftEdge: number;
  wbRightEdge: number;
  wbBottomEdge: number;
  edgeTolerance: number;
}

export class WhiteboardRenderer {
  private stage: Stage;
  private layer: Layer;
  private canvasContainer: HTMLDivElement;
  private minTopLeftPos: IPosition;
  private maxBottomRightPos: IPosition;
  private isAutoScrolling: boolean;

  constructor(private container: HTMLDivElement, private context: IWhiteboardRendererContext, options: { zoomRatio: number } = { zoomRatio: 1 }) {
    this.canvasContainer = document.createElement("div");
    container.appendChild(this.canvasContainer);
    // Set "smooth" behavior to reduce the speed of scroll when the user moving a shape
    setScrollBehavior(container, "smooth");

    // move the stage at [0.5, 0.5] to prevent issues with line thickness
    // see https://stackoverflow.com/questions/7530593/html5-canvas-and-line-width/7531540#7531540
    this.stage = new Stage({ container: this.canvasContainer, x: 0.5, y: 0.5, scaleX: options.zoomRatio, scaleY: options.zoomRatio, listening: !this.context.readonly });
    this.layer = new Layer({ listening: !this.context.readonly });

    if (options.zoomRatio === 1) {
      this.layer.imageSmoothingEnabled(false);
    }

    this.stage.add(this.layer);

    configureShapeSelection(this, this.layer, this.context);
    this.configureResize();

    if (!context.readonly) {
      configureShapeContextMenu(this, this.stage);
    }

    this.context.events.onLockWhiteboard(() => {
      this.stage.listening(false);
      const selectedShapes = this.selectedShapes();

      if (selectedShapes?.length) {
        this.selectedShapes([]);
        this.context.events.broadcastUnlockSelectedShapes(selectedShapes);
      }

      document.body.style.cursor = "default";
      this.stage.batchDraw();
    });
  }

  get viewportPosition(): IPosition {
    if (!this.minTopLeftPos) {
      return zeroPosition;
    }

    const scale = this.stage.scale();
    return {
      x: (this.minTopLeftPos.x + this.container.scrollLeft) / scale.x,
      y: (this.minTopLeftPos.y + this.container.scrollTop) / scale.y,
    };
  }

  get viewportSize(): ISize {
    const { width, top } = this.container.getBoundingClientRect();
    return {
      width,
      height: window.innerHeight - top,
    };
  }

  get minAndMaxPositions(): ISurface {
    return {
      topLeftPos: this.minTopLeftPos,
      bottomRightPos: this.maxBottomRightPos,
    };
  }

  getScale(): IPosition {
    return this.stage.scale();
  }

  selectAll(): void {
    const allShapes = this.layerShapes();
    if (allShapes.length) {
      this.selectedShapes(allShapes);
    }
  }

  destroy(): void {
    this.stage.destroy();
  }

  render(state: IWhiteboardState, options: IWhiteboardRenderOptions = {}): void {
    const existingShapeIds: Record<string, boolean> = {};
    const existingLinkIds: Record<string, boolean> = {};

    for (const shapeState of state.shapes) {
      if (shapeState.type === "goal") {
        sortMetricsByOrderId(shapeState.metrics);
      }

      this.renderShape(shapeState, options);
      existingShapeIds[shapeState.id] = true;
    }

    for (const link of state.links) {
      this.renderLink(link);
      existingLinkIds[linkId(link)] = true;
    }

    const toBeDestroyedShapeIds = this.layerShapes()
      .filter((shape) => !existingShapeIds[shape.id()])
      .map((shape) => shape.id());

    if (toBeDestroyedShapeIds.length) {
      // unselect shapes that are to be destroyed
      const selectedNotToBeDestroyedShapes = this.selectedShapes().filter((shape) => !toBeDestroyedShapeIds.includes(shape.id()));
      const hasToChangeSelection = selectedNotToBeDestroyedShapes.length !== this.selectedShapes().length;
      if (hasToChangeSelection) {
        this.selectedShapes(selectedNotToBeDestroyedShapes);
      }

      for (const shapeId of toBeDestroyedShapeIds) {
        const shape = this.findShape(shapeId);
        this.destroyShape(shape);
      }
    }

    for (const link of this.layerLinks()) {
      if (!existingLinkIds[link.id()]) {
        link.destroy();
      }
    }

    this.resizeInternal(options);
    this.stage.batchDraw();
  }

  renderSingleShape(state: IWhiteboardState, shapeId: string, options: IWhiteboardRenderOptions = {}): void {
    const shapeState = state.shapes.find((s) => s.id === shapeId);

    if (shapeState) {
      this.renderShape(shapeState, options);
    }
  }

  toDataURL(): string {
    return this.stage.toDataURL({ mimeType: "image/png" });
  }

  addEventListener<EventType extends WhiteboardEvent, UnderlyingEvent>(evt: EventType, listener: (ev: WhiteboardEventObject<EventType, UnderlyingEvent>) => void): this {
    addStateEventListener(this.stage, evt, listener);
    return this;
  }

  zoom(ratio: number, options: { relativeToMouse?: boolean } = {}): void {
    if (!ratio || ratio < 0) {
      return;
    }

    if (ratio !== 1) {
      this.layer.imageSmoothingEnabled(true);
    }

    const oldRatio = this.stage.scaleX();
    this.stage.scale({ x: ratio, y: ratio });

    // find relative coordinates to the center of the screen or mouse
    // so that we can position the stage proportionally
    const source = options.relativeToMouse ? this.stage.getPointerPosition() : { x: this.stage.width() / 2, y: this.stage.height() / 2 };
    const relatedTo = {
      x: (source.x - this.stage.x()) / oldRatio,
      y: (source.y - this.stage.y()) / oldRatio,
    };
    const newStagePos = {
      x: -(relatedTo.x - source.x / ratio) * ratio,
      y: -(relatedTo.y - source.y / ratio) * ratio,
    };
    this.stage.position(newStagePos);
    this.resize();

    this.scrollContainerTo({
      left: Math.max(0, -this.minTopLeftPos.x - newStagePos.x),
      top: Math.max(0, -this.minTopLeftPos.y - newStagePos.y),
    });
  }

  resize(): void {
    this.resizeInternal(defaultRenderOptions);
    this.stage.fire("resize");
    this.stage.batchDraw();
  }

  lockShapeById(shapeId: string, config: { color: string; userFirstName: string }): void {
    const shape = this.findShape(shapeId);
    if (!shape) {
      console.warn(`Trying to lock shape that doesn't exist. shapeId: ${shapeId}`);
      return;
    }

    this.lockShape(shape, config);
  }

  unlockShapes(...shapeIds: string[]): void {
    const shapes = shapeIds.reduce((shapesArr, shapeId) => {
      const shape = this.findShape(shapeId);
      if (!shape) {
        console.warn(`Trying to unlock shape that doesn't exist. shapeId: ${shapeId}`);
      } else {
        shapesArr.push(shape);
      }
      return shapesArr;
    }, []);
    this.unlockShape(...shapes);
  }

  fitToScreen(): number {
    const viewportSize = this.viewportSize;
    const layerClientRect = this.layerClientRect();

    const scaleWidth = viewportSize.width / (layerClientRect.width + viewportMargin.leftRight * 2);
    const scaleHeight = viewportSize.height / (layerClientRect.height + viewportMargin.topBottom * 2);

    const ratio = Math.round(Math.min(scaleWidth, scaleHeight) * 100);

    if (!ratio || ratio < 0) {
      return 100;
    }

    this.stage.scale({ x: ratio / 100, y: ratio / 100 });

    this.setMinAndMaxPositionsBasedOnLayerShapes({ useShapesCoords: true });
    this.setCanvasContainerSize();

    this.stage.fire("resize");
    this.stage.batchDraw();

    this.scrollContainerTo({ left: 0, top: 0 });
    // Manually set stage position in case there is no scroll (if container is already on 0, 0)
    this.setStagePositionToViewport();

    return ratio;
  }

  onMoveViewport(newViewportPosition: IPosition): void {
    const scale = this.getScale();
    const left = (newViewportPosition.x - this.viewportPosition.x) * scale.x;
    const top = (newViewportPosition.y - this.viewportPosition.y) * scale.y;

    // Set "auto" to avoid screen skipping when zoom-in/out
    setScrollBehavior(this.container, "auto");
    this.container.scrollBy({ left, top });
    setScrollBehavior(this.container, "smooth");
  }

  updateReactionSettingsInContext(reactionSettings: IReactionSettings): void {
    this.context.reactionSettings = reactionSettings;
  }

  onShapeDragEnd(): void {
    // Scroll container after dragend
    // On dragmove we move the stage we here we can calc the left/top scroll
    this.scrollContainerTo({
      left: -(this.stage.x() + this.minTopLeftPos.x),
      top: -(this.stage.y() + this.minTopLeftPos.y) + contextMenuTooltipTopMargin,
    });
  }

  private scrollContainerTo(options: ScrollToOptions = {}) {
    // Set "auto" to avoid screen skipping when zoom-in/out
    setScrollBehavior(this.container, "auto");
    this.container.scrollTo(options);
    setScrollBehavior(this.container, "smooth");
  }

  private resizeInternal(options: IWhiteboardRenderOptions): void {
    this.setMinAndMaxPositionsBasedOnLayerShapes();
    this.setCanvasContainerSize();
    this.setStageSizeToViewport();

    if (!options.keepCurrentViewport) {
      this.setStagePositionToViewport();
    }
  }

  private layerClientRect(): IRect {
    return this.layer.getClientRect({ relativeTo: this.stage });
  }

  private setStageSizeToViewport(): void {
    const size = this.viewportSize;
    this.stage.size(size);

    this.container.style.height = px(size.height);
    this.container.style.overflow = "auto";
  }

  private setMinAndMaxPositionsBasedOnLayerShapes(params: { useShapesCoords: boolean } = { useShapesCoords: false }) {
    const scale = this.stage.scale();
    const [shapesTopLeftPos, shapesBottomRightPos] = selectionBoundaries(...this.layerShapes());

    // TODO: Set limits on "viewportMargin.leftRight * scale.x" and "viewportMargin.topBottom * scale.y" because on small zoom ratio margin is really small and shapes goes behind menu
    this.minTopLeftPos = {
      x: Math.min(
        this.viewportPosition.x && !params.useShapesCoords ? this.viewportPosition.x * scale.x : Infinity,
        (shapesTopLeftPos.x - viewportMargin.leftRight) * scale.x
      ),
      y: Math.min(
        this.viewportPosition.y && !params.useShapesCoords ? this.viewportPosition.y * scale.y : Infinity,
        (shapesTopLeftPos.y - viewportMargin.topBottom) * scale.y
      ),
    };

    this.maxBottomRightPos = {
      x: Math.max(-Infinity, (shapesBottomRightPos.x + viewportMargin.leftRight) * scale.x),
      y: Math.max(-Infinity, (shapesBottomRightPos.y + viewportMargin.topBottom) * scale.y),
    };
  }

  private setCanvasContainerSize() {
    const size = areaSize(this.maxBottomRightPos, this.minTopLeftPos);
    const width = Math.max(this.viewportSize.width, size.width);
    const height = Math.max(this.viewportSize.height, size.height);

    this.canvasContainer.style.width = px(width * 2);
    this.canvasContainer.style.height = px(height * 2);
  }

  private setStagePositionToViewport() {
    this.stage.position({
      x: -(this.minTopLeftPos.x + this.container.scrollLeft),
      y: -(this.minTopLeftPos.y + this.container.scrollTop) + contextMenuTooltipTopMargin,
    });
  }

  private configureResize() {
    const checkAndChangeMinMaxPosition = (selTopLeftPos: IPosition, selBottomRightPos: IPosition, padding: number): void => {
      // Top
      const shouldReduceMinY = this.minTopLeftPos.y - selTopLeftPos.y - padding > minResizeCanvasContainerDistance;
      if (shouldReduceMinY) {
        selTopLeftPos.y = this.minTopLeftPos.y - minResizeCanvasContainerDistance;
      }

      // Right
      const shouldIncreaseMaxX = selBottomRightPos.x + padding - this.maxBottomRightPos.x > minResizeCanvasContainerDistance;
      if (shouldIncreaseMaxX) {
        selBottomRightPos.x = this.maxBottomRightPos.x + minResizeCanvasContainerDistance;
      }

      // Bottom
      const shouldIncreaseMaxY = selBottomRightPos.y + padding - this.maxBottomRightPos.y > minResizeCanvasContainerDistance;
      if (shouldIncreaseMaxY) {
        selBottomRightPos.y = this.maxBottomRightPos.y + minResizeCanvasContainerDistance;
      }

      // Left
      const shouldReduceMinX = this.minTopLeftPos.x - selTopLeftPos.x - padding > minResizeCanvasContainerDistance;
      if (shouldReduceMinX) {
        selTopLeftPos.x = this.minTopLeftPos.x - minResizeCanvasContainerDistance;
      }

      this.minTopLeftPos = {
        x: Math.min(this.minTopLeftPos.x, selTopLeftPos.x),
        y: Math.min(this.minTopLeftPos.y, selTopLeftPos.y),
      };

      this.maxBottomRightPos = {
        x: Math.max(this.maxBottomRightPos.x, selBottomRightPos.x),
        y: Math.max(this.maxBottomRightPos.y, selBottomRightPos.y),
      };
    };

    this.stage.on("dragmove", (evt) => {
      if (!evt.target.hasName("shape")) {
        return;
      }

      if (STOP_DRAG_SHAPE) {
        return;
      }

      // when moving shapes to the edges, scroll to their view
      const draggedShape = evt.target as unknown as WhiteboardShape;
      let selectedShapes = this.selectedShapes();
      if (!selectedShapes.some((shape) => shape === draggedShape)) {
        selectedShapes = [draggedShape];
      }

      const scale = this.stage.scale();
      const [selTopLeftPos, selBottomRightPos] = scalePositions(scale, selectionBoundaries(...selectedShapes));

      // leave some space at the edges so that it does not change
      // the scroll too rapidly
      const padding = 10 * scale.x;

      const newStagePosition: Partial<IPosition> = {};

      const isTopSide = selTopLeftPos.y - padding < -this.stage.y();
      if (isTopSide) {
        newStagePosition.y = this.stage.y() + minResizeCanvasContainerDistance;
      }

      const isRightSide = selBottomRightPos.x + padding > -this.stage.x() + this.stage.width();
      if (isRightSide) {
        newStagePosition.x = this.stage.x() - minResizeCanvasContainerDistance;
      }

      const isBottomSide = selBottomRightPos.y + padding > -this.stage.y() + this.stage.height();
      if (isBottomSide) {
        newStagePosition.y = this.stage.y() - minResizeCanvasContainerDistance;
      }

      const isLeftSide = selTopLeftPos.x - padding < -this.stage.x();
      if (isLeftSide) {
        newStagePosition.x = this.stage.x() + minResizeCanvasContainerDistance;
      }

      checkAndChangeMinMaxPosition(selTopLeftPos, selBottomRightPos, padding);

      if (Object.keys(newStagePosition).length && !STOP_DRAG_SHAPE) {
        STOP_DRAG_SHAPE = true;

        if (!newStagePosition.x) newStagePosition.x = this.stage.x();
        if (!newStagePosition.y) newStagePosition.y = this.stage.y();

        this.setCanvasContainerSize();
        this.stage.position(<IPosition>newStagePosition);
        this.stage.fire("resize", evt);
        this.stage.batchDraw();

        resetAbilityToDragShape();
      }
    });

    this.container.addEventListener("scroll", () => {
      this.resize();
      this.stage.fire("state-scroll");

      const { scrollLeft, scrollTop } = this.container;
      this.canvasContainer.style.transform = `translate(${px(scrollLeft)}, ${px(scrollTop)})`;
    });
  }

  private renderLink(linkState: IWhiteboardLinkState) {
    const sourceShape = this.findShape(linkState.source.id);
    if (!sourceShape) {
      console.warn(`Cannot find shape ${linkState.source.id} for the link source`);
      return;
    }

    const targetShape = this.findShape(linkState.target.id);
    if (!targetShape) {
      console.warn(`Cannot find shape ${linkState.target.id} for the link target`);
      return;
    }

    const id = linkId(linkState);
    let link = this.findLink(id);
    if (link) {
      link.link = linkState;
      link.connectShapes(sourceShape, targetShape);
      return;
    }

    link = createLinkInstanceFromState(linkState, this.context);
    if (link) {
      link.setId(linkId(linkState)).addName("link");
      this.layer.add(link);
      link.connectShapes(sourceShape, targetShape);
    }
  }

  private renderShape(shapeState: IWhiteboardShapeState, options?: IWhiteboardRenderOptions): void {
    let shape = this.findShape(shapeState.id);

    if (shape) {
      if (!equals(shape.state, shapeState) && shape.updateState(shapeState, options)) {
        this.layer.batchDraw();
      }
    } else {
      shape = createShapeInstanceFromState(shapeState, this.context, this.stage, this.layer);
      if (shape) {
        shape.setId(shapeState.id).addName("shape");
        this.layer.add(shape);
        this.layer.batchDraw();
      }
    }

    if (shape) {
      shape.setZIndex(shapeState.zIndex);
      this.adjustLockIfLocked(shape);
    }
  }

  private layerShapes() {
    return [...this.layer.find<WhiteboardShape>(".shape")];
  }

  private layerLinks() {
    return [...this.layer.find<WhiteboardShape>(".link")];
  }

  private findShape(id: string): WhiteboardShape {
    return this.layer.findOne(`#${id}`);
  }

  private findLink(id: string): WhiteboardLink {
    return this.layer.findOne(`#${id}`);
  }

  private destroyShape(shape: WhiteboardShape) {
    // remove any associated links
    for (const link of this.layerLinks()) {
      if (link.id().includes(shape.id())) {
        link.destroy();
      }
    }

    shape.hide();
    this.unlockShape(shape);

    shape.destroy();
  }

  onStopAutoScrolling(): void {
    this.isAutoScrolling = false;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  onStartAutoScrolling(e): void {
    const event = e.evt || e;
    this.isAutoScrolling = true;

    let timer = null;
    // Tolerance area where the mouse pointer should land to start auto scroll
    const edgeTolerance = 60;

    const scrollBarWidth = 17;

    const wbDimmenssions = this.container?.getBoundingClientRect();
    const wbWidth = wbDimmenssions.width;
    const wbHeight = wbDimmenssions.height;
    const wbTopBorder = wbDimmenssions.top;
    const wbLeftBorder = wbDimmenssions.left;
    const wbBottomBorder = wbTopBorder + wbHeight;
    const wbRightBorder = wbLeftBorder + wbWidth;

    const viewportX = event.clientX;
    const viewportY = event.clientY;

    const viewportWidth = this.container?.clientWidth;
    const viewportHeight = this.container?.clientHeight;

    const wbTopEdge = wbTopBorder + edgeTolerance;
    const wbLeftEdge = wbLeftBorder + edgeTolerance;
    const wbBottomEdge = wbBottomBorder - (edgeTolerance + scrollBarWidth);
    const wbRightEdge = wbRightBorder - (edgeTolerance + scrollBarWidth);

    const isInLeftEdge = viewportX < wbLeftEdge;
    const isInRightEdge = viewportX > wbRightEdge;
    const isInTopEdge = viewportY < wbTopEdge;
    const isInBottomEdge = viewportY > wbBottomEdge;

    // If the mouse is not in the viewport edge, there's no need to calculate
    if (!(isInLeftEdge || isInRightEdge || isInTopEdge || isInBottomEdge)) {
      clearTimeout(timer);
      return;
    }

    const documentWidth = Math.max(this.container?.scrollWidth, this.container?.offsetWidth, this.container?.clientWidth);

    const documentHeight = Math.max(this.container?.scrollHeight, this.container?.offsetHeight, this.container?.clientHeight);

    // Calculate the maximum scroll offset in each direction.
    const maxScrollLeft = documentWidth - viewportWidth;
    const maxScrollTop = documentHeight - viewportHeight;

    // As we examine the mousemove event, we want to adjust the window scroll in
    // immediate response to the event; but, we also want to continue adjusting
    // the window scroll if the user rests their mouse in the edge boundary.
    let checkForScroll;

    const params: AdjustScrollParams = {
      viewportY,
      viewportX,
      maxScrollTop,
      maxScrollLeft,
      isInLeftEdge,
      isInRightEdge,
      isInTopEdge,
      isInBottomEdge,
      wbTopEdge,
      wbLeftEdge,
      wbRightEdge,
      wbBottomEdge,
      edgeTolerance,
    };

    (checkForScroll = (): void => {
      clearTimeout(timer);
      if (this.isAutoScrolling && this.adjustScroll(params)) {
        timer = setTimeout(checkForScroll, 50);
      }
    })();
  }

  // Adjust the window scroll based on the user's mouse position. Returns True
  // or False depending on whether or not the window scroll was changed.
  adjustScroll = (params: AdjustScrollParams): boolean => {
    const {
      viewportX,
      viewportY,
      maxScrollTop,
      maxScrollLeft,
      isInLeftEdge,
      isInRightEdge,
      isInTopEdge,
      isInBottomEdge,
      wbTopEdge,
      wbLeftEdge,
      wbRightEdge,
      wbBottomEdge,
      edgeTolerance,
    } = params;
    // Get the current scroll position of the document.
    const currentScrollLeft = this.container?.scrollLeft || 0;
    const currentScrollTop = this.container?.scrollTop || 0;

    // Determine if the window can be scrolled in any particular direction.
    const canScrollUp = currentScrollTop > 0;
    const canScrollDown = currentScrollTop < maxScrollTop;
    const canScrollLeft = currentScrollLeft > 0;
    const canScrollRight = currentScrollLeft < maxScrollLeft;

    // Since we can potentially scroll in two directions at the same time,
    // let's keep track of the next scroll, starting with the current scroll.
    let nextScrollLeft = currentScrollLeft;
    let nextScrollTop = currentScrollTop;

    const maxScrollStep = 2;

    // Should we scroll left?
    if (isInLeftEdge && canScrollLeft) {
      const intensity = (wbLeftEdge - viewportX) / edgeTolerance;
      nextScrollLeft = nextScrollLeft - maxScrollStep * intensity;
      // Should we scroll right?
    } else if (isInRightEdge && canScrollRight) {
      const intensity = (viewportX - wbRightEdge) / edgeTolerance;
      nextScrollLeft = nextScrollLeft + maxScrollStep * intensity;
    }
    // Should we scroll up?
    if (isInTopEdge && canScrollUp) {
      const intensity = (wbTopEdge - viewportY) / edgeTolerance;
      nextScrollTop = nextScrollTop - maxScrollStep * intensity;
      // Should we scroll down?
    } else if (isInBottomEdge && canScrollDown) {
      const intensity = (viewportY - wbBottomEdge) / edgeTolerance;
      nextScrollTop = nextScrollTop + maxScrollStep * intensity;
    }

    // Sanitize invalid maximums.
    nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, nextScrollLeft));
    nextScrollTop = Math.max(0, Math.min(maxScrollTop, nextScrollTop));

    if (nextScrollLeft !== currentScrollLeft || nextScrollTop !== currentScrollTop) {
      this.scrollContainerTo({
        top: nextScrollTop,
        left: nextScrollLeft,
      });
      return true;
    }
    return false;
  };
}
